import { useState, useRef } from "react";
import api from "../../api/client";

export default function CsvImportModal({ onClose, onRefresh, importType = "FSOI" }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [step, setStep] = useState("upload");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState("");

  const isActive = importType === "ACTIVE";
  const endpoint = isActive ? "/xlsx-imports/import-active" : "/xlsx-imports/import";

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      setStep("done");
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setStep("upload");
    setImportResult(null);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-lg font-semibold mb-4">
          {isActive ? "Import Active" : "Import FSOI"}
        </h2>

        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Upload XLSX File <span className="text-red-500">*</span>
          </label>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            disabled={step !== "upload"}
            onChange={(e) => setFile(e.target.files[0] || null)}
            className="block w-full text-sm disabled:opacity-50"
          />
          <p className="text-xs text-gray-400 mt-1">Only .xlsx files are accepted.</p>
        </div>

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

        {importResult && (
          <div className="bg-gray-50 rounded p-3 mb-4 text-sm">
            <p className="font-medium mb-2">Import Summary</p>
            <p className="text-gray-700">Total Rows Processed: {importResult.total}</p>
            <p className="text-green-700">
              Successfully Imported: {importResult.successfully_imported}
            </p>
            <p className="text-yellow-700">Skipped: {importResult.skipped}</p>
            {isActive ? (
              <>
                <p className="text-blue-700">
                  Matched Dashboard JOs: {importResult.matched_count}
                </p>
                {importResult.staging_match_count > 0 && (
                  <p className="text-cyan-700">
                    Matched FSOI Staging: {importResult.staging_match_count}
                  </p>
                )}
                <p className="text-indigo-700">
                  New Job Orders Created: {importResult.new_jo_count}
                </p>
                <p className="text-gray-500">
                  Non-BEST Agency: {importResult.non_best_count}
                </p>
              </>
            ) : (
              <>
                {importResult.non_best_count > 0 && (
                  <p className="text-gray-500">
                    Non-BEST Agency (skipped): {importResult.non_best_count}
                  </p>
                )}
                <p className="text-orange-600">
                  Duplicate JO Numbers: {importResult.duplicate_jo_count}
                </p>
                {importResult.backfilled_count > 0 && (
                  <p className="text-blue-700">
                    JO Numbers Backfilled: {importResult.backfilled_count}
                  </p>
                )}
                <p className="text-red-600">
                  Missing Required Fields: {importResult.missing_field_count}
                </p>
              </>
            )}
            {importResult.errors.length > 0 && (
              <div className="mt-2 max-h-40 overflow-y-auto text-xs text-red-600 border-t pt-2">
                <p className="font-medium mb-1">Row Errors:</p>
                {importResult.errors.map((e, i) => (
                  <p key={i}>{e}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {step === "done" && (
          <div className="bg-green-50 border border-green-300 text-green-700 text-sm rounded px-3 py-2 mb-3">
            {isActive
              ? "Active import completed. BEST agency records have been synced to the dashboard."
              : "Import completed. BEST agency records are now in the staging table for review."}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">
            Close
          </button>
          {step === "upload" && (
            <button
              onClick={handleImport}
              disabled={!file || importing}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {importing ? "Importing..." : "Import"}
            </button>
          )}
          {step === "done" && (
            <button onClick={handleReset} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">
              Import Another File
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
